import { useState } from "react";
import { ApiError } from "@/lib/api.js";
import { useTenant } from "@/lib/tenant-context.js";
import { classifyError, type WalkinState } from "@/lib/walkin.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Select } from "@/components/ui/select.js";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { useToast } from "@/components/ui/toast.js";
import type { Customer, Resource, Service } from "@/lib/types.js";

/**
 * Walk-in flow per Flow D + REQ-014, REQ-015.
 *
 *   Initial submit: override_overlap = false → may receive 409 walkin_conflict.
 *   On 409: render the conflicting stack + an explicit "Confirm walk-in despite conflicts" button.
 *   Re-submit with override_overlap = true; conflicting holds become cancelled_by_walkin.
 */
export function WalkinDialog({
  open,
  onOpenChange,
  customers,
  services,
  resources
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  customers: Customer[];
  services: Service[];
  resources: Resource[];
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [state, setState] = useState<WalkinState>({ status: "idle" });
  const [form, setForm] = useState({
    customer_id: customers[0]?.id ?? "",
    service_id: services[0]?.id ?? "",
    primary_resource_id: resources[0]?.id ?? "",
    starts_at: ""
  });

  async function submit(override: boolean): Promise<void> {
    setState({ status: "submitting" });
    const picked = customers.find((c) => c.id === form.customer_id);
    try {
      const appointment = await api.createWalkin({
        customer_id: form.customer_id,
        customer_name: picked?.name,
        customer_phone: picked?.phone_e164 ?? undefined,
        service_id: form.service_id,
        primary_resource_id: form.primary_resource_id,
        starts_at: form.starts_at || undefined,
        override_overlap: override
      });
      setState({ status: "success", appointment });
      toast({ title: "Walk-in created", variant: "success" });
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiError) {
        setState(classifyError(error));
        if (error.code !== "walkin_conflict") {
          toast({ title: "Walk-in failed", description: error.message, variant: "destructive" });
        }
      } else {
        setState({ status: "error", message: String(error), code: "unknown" });
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create walk-in</DialogTitle>
          <DialogDescription>Bypass the slot grid for an in-person arrival.</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(false);
          }}
        >
          <Field label="Customer">
            <Select value={form.customer_id} onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value }))}>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Service">
            <Select value={form.service_id} onChange={(e) => setForm((f) => ({ ...f, service_id: e.target.value }))}>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.duration_minutes}m)
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Resource">
            <Select value={form.primary_resource_id} onChange={(e) => setForm((f) => ({ ...f, primary_resource_id: e.target.value }))}>
              {resources.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Starts at">
            <Input
              type="datetime-local"
              value={form.starts_at}
              onChange={(e) => setForm((f) => ({ ...f, starts_at: localToIso(e.target.value) }))}
            />
          </Field>

          {state.status === "conflict" && (
            <div className="rounded border border-warning/40 bg-warning/10 p-3 text-xs">
              <div className="font-medium">Slot conflicts detected</div>
              <ul className="mt-1 list-disc pl-4">
                {state.conflicting_appointments.map((a) => (
                  <li key={a.id}>
                    Appointment {a.id.slice(0, 8)} — {a.starts_at} → {a.ends_at}
                  </li>
                ))}
                {state.conflicting_holds.map((h) => (
                  <li key={h.id}>
                    Active hold {h.id.slice(0, 8)} — will be cancelled if you override
                  </li>
                ))}
              </ul>
              <div className="mt-2">
                Confirming with override stacks the walk-in onto the slot. Conflicting holds
                will transition to <code>cancelled_by_walkin</code>.
              </div>
            </div>
          )}

          <DialogFooter>
            {state.status === "conflict" ? (
              <>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  type="button"
                  onClick={() => void submit(true)}
                  disabled={state.status === "conflict" ? false : (state as WalkinState).status === "submitting"}
                >
                  Confirm with override
                </Button>
              </>
            ) : (
              <Button type="submit" disabled={state.status === "submitting"}>
                {state.status === "submitting" ? "Creating…" : "Create walk-in"}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function localToIso(localValue: string): string {
  if (!localValue) return "";
  return new Date(localValue).toISOString();
}
